import { useState } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import EditDialog from './EditDialog'
import ConfirmDialog from './ConfirmDialog'
import { useAPIUpsertSKP } from 'api/kinerja/useAPIKinerja'
import {
  isValidPangkat,
  checkPangkatAndJenjang,
  getPangkatGolongan,
} from 'app/KinerjaPage/utils/pangkat'
import { isValidJenjang, mapJenjangName } from 'app/KinerjaPage/utils/jenjang'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'

import type { HTMLAttributes } from 'react'
import type { Jenjang, KinerjaProfil } from 'types/kinerja'

interface PanelProfilProps {
  profile: KinerjaProfil
}

interface PanelProfileListItemProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  value: string
  testId?: string
}

export const WARNING_TEXT_PNS_PPPK = `Pastikan Jenjang Jabatan sesuai karena akan memengaruhi Perencanaan Kinerja. Saat ini data hanya bisa diedit satu kali saja, ya.`
export const WARNING_TEXT_NON_PNS_PPPK = `Silakan hubungi operator sekolah untuk pembaruan data diri Anda di Dapodik.`

function PanelListItem({
  label,
  value,
  testId,
  className,
  ...props
}: PanelProfileListItemProps) {
  return (
    <div className={clsx('flex gap-2', className)} {...props}>
      <Text variant="body" className="mb-1 w-48">
        {label}
      </Text>
      <Text variant="body-bold">:</Text>
      <Text data-testid={testId} variant="body-bold">
        {value}
      </Text>
    </div>
  )
}

export default function PanelProfil({ profile }: PanelProfilProps) {
  // create a boolean state to open dialog edit profile
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [currentProfile, setCurrentProfile] = useState(profile)
  const [isModified, setIsModified] = useState(false)

  const { mutateAsync: createNewSKP, isLoading } = useAPIUpsertSKP()

  const router = useRouter()
  const toast = useToast()

  // If the initial profile is not valid, (i.e. wrong pangkat or pangkat and jenjang does not match), we ask the user to modify it.
  // But we won't validate it again after the user modified it.

  const pangkatGolongan =
    isModified || isValidPangkat(currentProfile.pangkatGolongan)
      ? currentProfile.pangkatGolongan
      : '-'

  let jenjang = '-'

  if (isModified) {
    jenjang = currentProfile.jenjang
  } else if (currentProfile.kategoriPegawai === 'PNS') {
    jenjang = checkPangkatAndJenjang(
      currentProfile.pangkatGolongan,
      currentProfile.jenjang.toLowerCase() as Jenjang
    )
      ? currentProfile.jenjang
      : '-'
  } else if (currentProfile.kategoriPegawai === 'PPPK') {
    jenjang = isValidJenjang(currentProfile.jenjang)
      ? currentProfile.jenjang
      : '-'
  }

  let isIncomplete = false

  if (currentProfile.kategoriPegawai === 'PNS') {
    isIncomplete = pangkatGolongan === '-' || jenjang === '-'
  } else if (currentProfile.kategoriPegawai === 'PPPK') {
    isIncomplete = jenjang === '-'
  }

  const isGuruStatusUndefined = currentProfile.kategoriPegawai === 'UNDEFINED'

  const handleSave = (profile: KinerjaProfil) => {
    setIsModified(true)
    setCurrentProfile(profile)
  }

  const handleConfirm = async () => {
    try {
      await createNewSKP({
        pangkatGolongan: currentProfile.pangkatGolongan,
        jenjang: currentProfile.jenjang,
      })

      setIsConfirmDialogOpen(false)

      router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan`)
    } catch (error) {
      console.error(error)

      const message =
        error?.originalError?.response?.data?.error?.message ?? error.toString()

      toast({
        message: `Gagal mengubah profil: ${message}`,
      })
    }
  }

  return (
    <div className="p-9">
      <div className="rounded-lg bg-white mt-9 max-w-[1216px] m-auto">
        <div className="p-9 border-b">
          <Text variant="display-sm" className="mb-1">
            Cek data diri Anda
          </Text>

          <div className="flex p-4 gap-3 items-center rounded bg-surface-warning-subdued mb-6">
            <Icon>information_outlined</Icon>
            <Text variant="body">
              {isGuruStatusUndefined
                ? WARNING_TEXT_NON_PNS_PPPK
                : WARNING_TEXT_PNS_PPPK}
            </Text>
          </div>

          <div className="flex flex-col gap-3">
            <PanelListItem
              testId="nama"
              label="Nama"
              value={currentProfile.nama}
            />
            {currentProfile.kategoriPegawai !== 'UNDEFINED' && (
              <PanelListItem
                testId="nip"
                label="NIP"
                value={currentProfile.nip || '-'}
              />
            )}

            <PanelListItem
              testId="status-kepegawaian"
              label="Status Kepegawaian"
              value={currentProfile.statusPegawai || '-'}
            />

            {currentProfile.kategoriPegawai === 'PNS' && (
              <PanelListItem
                testId="pangkat"
                label="Pangkat, Golongan Ruang"
                value={getPangkatGolongan(pangkatGolongan)}
              />
            )}

            {currentProfile.kategoriPegawai !== 'UNDEFINED' && (
              <PanelListItem
                testId="jenjang"
                label="Jenjang Jabatan"
                value={mapJenjangName(jenjang)}
              />
            )}

            <PanelListItem
              testId="satuan-pendidikan"
              label="Satuan Pendidikan Induk"
              value={currentProfile.satuanPendidikan || '-'}
            />
          </div>
        </div>
        <div className="flex py-4 px-7">
          <div className="ml-auto flex gap-5">
            {isIncomplete && !isGuruStatusUndefined ? (
              <Button
                size="lg"
                color="black"
                onClick={() => setIsDialogOpen(true)}
              >
                Lengkapi data diri
              </Button>
            ) : (
              <>
                {!isGuruStatusUndefined && (
                  <Button
                    size="lg"
                    color="white"
                    onClick={() => setIsDialogOpen(true)}
                  >
                    Edit
                  </Button>
                )}
                <Button size="lg" onClick={() => setIsConfirmDialogOpen(true)}>
                  Data sudah sesuai
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <EditDialog
        profile={currentProfile}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        onSave={handleSave}
      />

      <ConfirmDialog
        profile={currentProfile}
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleConfirm}
        disabledConfirmButton={isLoading}
        isLoading={isLoading}
      />
    </div>
  )
}
